import React, { useEffect, useMemo } from 'react';
import { Location } from '../../../XV2';
import { Outlet } from '../../../XV2';
import { XDrawerBottom } from '../../x/drawer-bottom/XDrawerBottom';
import { CovidForm } from '../../covid/form/CovidForm';
import { useFormImmer } from '../../../hooks/form-immer';
import { useMeta } from '../../../hooks/meta';
import { XButton } from '../../x/button/XButton';
import { UiGrid, UiFlex } from '@open-stack/ui';
import { useApiQuery } from '@orderx/lab/xr/http';
import { XRouterRedirect } from '../../x/router/redirect/XRouterRedirect';
import { LocationPage } from '../../../pages/location/LocationPage';
import { RootIdPage } from '../../../pages/id/RootIdPage';
import { Zone } from '../../../XV2';
import { BrowsePage } from '../../../pages/browse/BrowsePage';
import { XCard } from '../../x/card/XCard';
import { useCovidRegistration } from '../../../hooks/covid-registration';

interface Props {
    tagId: string;
    location: Location;
    outlet: Outlet;
    isZoneRequired: boolean;
}

export function TagCovidRegistration({ location, outlet, isZoneRequired }: Props) {
    const covidRegistration = useCovidRegistration();
    const {
        meta: { areaCode },
    } = useMeta();

    const locationId = location?.id || outlet?.locationId;

    const covidForm = useFormImmer({
        name: '',
        areaCode,
        phone: '',
        isConfirmed: false,
        isAutoRegister: true,
        locationId,
        outletId: outlet?.id,
        zoneId: undefined,
        zoneName: undefined,
    });

    const { form } = covidForm;

    const zone = useMemo<Zone | undefined>(
        () => form.zoneId && outlet.zones.find((zone) => zone.id === form.zoneId),
        [form.zoneId],
    );

    const isValid = form.name && form.phone.length > 5 && form.isConfirmed;

    const register = useApiQuery(async () => {
        await covidRegistration.queryRegister.run({
            name: form.name,
            areaCode: form.areaCode,
            phone: form.phone,
            locationId: location?.id,
            locationName: location?.name,
            outletId: form.outletId,
            outletName: outlet?.name,
            zoneId: form.zoneId,
            zoneName: zone?.name,
            isAutoRegister: form.isAutoRegister,
        });

        covidRegistration.setIsAutoRegister(form.isAutoRegister);

        return true;
    });

    useEffect(() => {
        if (areaCode && !form.areaCode) {
            covidForm.updateField('areaCode')(areaCode);
        }
    }, [areaCode, form.areaCode]);

    useEffect(() => {
        if (zone) {
            covidForm.updateField('zoneName')(zone.name);
        }
    }, [zone]);

    const isRegistered = register.response;

    if (isRegistered) {
        if (zone) {
            return <XRouterRedirect pageOrPath={RootIdPage} params={{ id: zone.hash }} />;
        }

        return <XRouterRedirect pageOrPath={LocationPage} params={{ locationId }} />;
    }

    return (
        <XDrawerBottom onClose={() => {}}>
            <XCard>
                <UiGrid padding={4} gap={4}>
                    <CovidForm
                        form={covidForm}
                        zoneOptions={
                            isZoneRequired ? outlet?.zones.map((item) => ({ label: item.name, value: item.id })) : []
                        }
                    />

                    <UiFlex gap={1} flexChildren>
                        <XButton to={BrowsePage.route} cta fullWidth variant="outlined">
                            Cancel
                        </XButton>

                        <XButton
                            onClick={register.run}
                            isLoading={register.isLoading}
                            cta
                            color="primary"
                            disabled={!isValid}
                            fullWidth>
                            Confirm
                        </XButton>
                    </UiFlex>
                </UiGrid>
            </XCard>
        </XDrawerBottom>
    );
}
